import { Ellipsis } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Popover, type PopoverAnchor, type PopoverCloseReason } from './Popover.js';

export interface MenuItem {
  readonly label: string;
  readonly onSelect: () => void;
  /** A destructive item reads as one; it still asks before it acts. */
  readonly danger?: boolean;
  readonly disabled?: boolean;
}

export interface MenuSurfaceProps {
  anchor: PopoverAnchor;
  items: readonly MenuItem[];
  /** Absent where the menu closed because an item was chosen. */
  onClose: (reason?: PopoverCloseReason) => void;
  align?: 'start' | 'end';
  /** What the menu is a menu of, for a screen reader. */
  label?: string;
}

/**
 * The list of commands on its own, so a right-click can raise it at a point
 * with no trigger to hang from. Focus really moves here, unlike the select: a
 * menu item is a command, and a command is a button.
 */
export function MenuSurface(props: MenuSurfaceProps): React.JSX.Element {
  const itemNodes = useRef<(HTMLButtonElement | null)[]>([]);
  const { items } = props;

  useEffect(() => {
    const first = items.findIndex((item) => item.disabled !== true);
    itemNodes.current[first === -1 ? 0 : first]?.focus();
  }, [items]);

  function moveFocus(from: number, delta: number): void {
    const count = items.length;
    let index = from;
    for (let tries = 0; tries < count; tries += 1) {
      index = (index + delta + count) % count;
      if (items[index]?.disabled !== true) {
        itemNodes.current[index]?.focus();
        return;
      }
    }
  }

  return (
    <Popover anchor={props.anchor} align={props.align ?? 'end'} onClose={props.onClose}>
      <div role="menu" aria-label={props.label} className="menu">
        {items.map((item, index) => (
          <button
            key={item.label}
            ref={(node) => {
              itemNodes.current[index] = node;
            }}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            className={item.danger === true ? 'menu-item danger' : 'menu-item'}
            onClick={() => {
              props.onClose();
              item.onSelect();
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveFocus(index, 1);
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveFocus(index, -1);
              }
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </Popover>
  );
}

export interface MenuProps {
  items: readonly MenuItem[];
  /** The accessible name of the button, since "⋯" says nothing out loud. */
  label: string;
  className?: string;
  /** What the trigger reads; the three dots unless something else is wanted. */
  children?: React.ReactNode;
  align?: 'start' | 'end';
}

/** A "⋯" button and the commands behind it. */
export function Menu(props: MenuProps): React.JSX.Element {
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        ref={setTrigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={props.label}
        title={props.label}
        className={props.className === undefined ? 'icon-button' : `icon-button ${props.className}`}
        onClick={() => setOpen((current) => !current)}
      >
        {props.children ?? <Ellipsis aria-hidden="true" />}
      </button>

      {open && trigger !== null && (
        <MenuSurface
          anchor={{ kind: 'element', element: trigger }}
          align={props.align ?? 'end'}
          items={props.items}
          label={props.label}
          onClose={(reason) => {
            setOpen(false);
            // A click elsewhere already chose where focus goes.
            if (reason !== 'outside') trigger.focus();
          }}
        />
      )}
    </>
  );
}
